import { Suspense, lazy, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { Logo } from '@/components/Logo';
import { Reveal } from './Reveal';
import { SpotlightCard } from './SpotlightCard';
import { Marquee } from './Marquee';
import { CountUp } from './CountUp';
import { MagneticButton } from './MagneticButton';
import { usePointerParallax } from './useParallax';

// Lazy-load the animated network field so it never blocks the hero's LCP text.
const NetworkField = lazy(() =>
  import('./NetworkField').then((m) => ({ default: m.NetworkField })),
);

/**
 * Premium public landing. Cinematic dark hero with a living distribution
 * network (packets flow between hubs) that reacts to the pointer, a live
 * operations strip, a brand marquee, an asymmetric capability bento with
 * cursor-tracking spotlight cards, a 3D platform preview with scroll parallax,
 * and a bold closing band. Spanish-first (hero copy from docs/12). Brand logo
 * and final palette are PENDING; everything is token-driven. All motion has a
 * reduced-motion fallback and animates only transform/opacity.
 */
export function LandingPage() {
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  return (
    <div className="min-h-dvh bg-brand-bg text-brand-white">
      {/* Reading-progress hairline. */}
      <motion.div
        style={{ scaleX: progress }}
        className="fixed inset-x-0 top-0 z-50 h-0.5 origin-left bg-brand-accent"
        aria-hidden="true"
      />

      <SiteHeader />
      <Hero reduced={reduced ?? false} />
      <BrandMarquee />
      <Capabilities />
      <ProcessSection />
      <PlatformSection />
      <IndustriesSection />
      <FinalCta />
      <SiteFooter />
    </div>
  );
}

/* ---------------------------------- hero --------------------------------- */

function Hero({ reduced }: { reduced: boolean }) {
  const { x, y } = usePointerParallax();
  // Network drifts one way, the ambient glow the other, for layered depth.
  const fieldX = useTransform(x, [-1, 1], [20, -20]);
  const fieldY = useTransform(y, [-1, 1], [14, -14]);

  return (
    <section className="bg-brand-gradient hero-aurora relative overflow-hidden">
      <motion.div
        style={reduced ? undefined : { x: fieldX, y: fieldY }}
        className="pointer-events-none absolute inset-0 scale-110 opacity-70"
      >
        <Suspense fallback={null}>
          <NetworkField reduced={reduced} />
        </Suspense>
      </motion.div>

      {/* Fade the field into the section seam below. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-brand-bg" />

      <div className="relative mx-auto max-w-6xl px-6 pb-28 pt-24 sm:pt-32">
        <motion.span
          initial={reduced ? false : { opacity: 0, y: 12 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 rounded-full border border-brand-silver/20 bg-brand-white/5 px-3 py-1 text-xs font-medium text-brand-silver backdrop-blur"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-accent opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-accent" />
          </span>
          Grupo de distribución industrial
        </motion.span>

        <h1
          className="mt-6 max-w-3xl font-semibold text-glow-gold"
          style={{
            fontSize: 'clamp(2.5rem, 5.6vw, 4.75rem)',
            lineHeight: 1.03,
            letterSpacing: '-0.02em',
            textWrap: 'balance',
          }}
        >
          <WordReveal reduced={reduced} words={HEADLINE} />
        </h1>

        <motion.p
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-xl text-lg leading-relaxed text-brand-silver"
        >
          Lubricantes, fluidos industriales y soluciones comerciales. Importación, crédito,
          logística y pagos en una sola plataforma B2B.
        </motion.p>

        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.62, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <MagneticButton
            to="/request-account"
            className="press inline-flex h-12 items-center justify-center rounded-md bg-brand-accent px-6 text-sm font-semibold text-on-accent shadow-[0_10px_40px_-12px_rgba(216,164,49,0.7)] transition-colors hover:bg-accent-hover"
          >
            Solicitar cuenta
          </MagneticButton>
          <Link
            to="/request-account"
            className="press inline-flex h-12 items-center justify-center rounded-md border border-brand-silver/30 px-6 text-sm font-semibold text-brand-white transition-colors hover:border-brand-silver/60 hover:bg-brand-white/5"
          >
            Convertirse en distribuidor
          </Link>
        </motion.div>

        {/* Live operations strip — the platform's pulse, not a hero-metric grid. */}
        <motion.dl
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.74, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 grid max-w-2xl grid-cols-3 gap-px overflow-hidden rounded-lg border border-brand-silver/15 bg-brand-silver/10 text-center sm:text-left"
        >
          {LIVE_METRICS.map((m) => (
            <div key={m.label} className="bg-brand-bg/70 px-4 py-4">
              <dd className="text-xl font-semibold tabular-nums text-brand-white sm:text-2xl">
                <CountUp to={m.to} prefix={m.prefix} suffix={m.suffix} decimals={m.decimals} />
              </dd>
              <dt className="mt-1 text-[11px] leading-tight text-brand-silver">{m.label}</dt>
            </div>
          ))}
        </motion.dl>
      </div>
    </section>
  );
}

/** Per-word mask reveal: each word slides up from behind a clip. */
function WordReveal({ words, reduced }: { words: { t: string; gold?: boolean }[]; reduced: boolean }) {
  if (reduced) {
    return (
      <>
        {words.map((w, i) => (
          <span key={i} className={w.gold ? 'text-brand-accent' : undefined}>
            {w.t}
            {i < words.length - 1 ? ' ' : ''}
          </span>
        ))}
      </>
    );
  }
  return (
    <>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className={`inline-block ${w.gold ? 'text-brand-accent' : ''}`}
            initial={{ y: '110%' }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
          >
            {w.t}
          </motion.span>
          {i < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </>
  );
}

/* -------------------------------- marquee -------------------------------- */

function BrandMarquee() {
  return (
    <div className="border-y border-brand-silver/10 bg-brand-bg py-6">
      <Marquee
        durationSec={42}
        items={CATEGORIES.map((c) => (
          <span
            key={c}
            className="mx-8 inline-flex items-center gap-3 text-sm font-medium uppercase tracking-[0.18em] text-brand-silver/70"
          >
            <span className="h-1 w-1 rounded-full bg-brand-accent" />
            {c}
          </span>
        ))}
      />
    </div>
  );
}

/* ------------------------------ capabilities ----------------------------- */

function Capabilities() {
  const feature = CAPABILITIES[0]!;
  const secondary = CAPABILITIES[1]!;
  const rest = CAPABILITIES.slice(2);
  return (
    <Section id="capacidades">
      <Reveal>
        <SectionHeading
          title="Una operación de distribución seria, de extremo a extremo."
          lead="Desde la importación hasta la entrega y el cobro, cada parte del negocio vive en la misma plataforma."
        />
      </Reveal>

      {/* Asymmetric bento: one dominant feature + a tall partner, then a trio. */}
      <div className="mt-12 grid gap-4 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <SpotlightCard className="flex h-full flex-col justify-between p-7">
            <div>
              <CapIcon name={feature.icon} />
              <h3 className="mt-5 text-2xl font-semibold tracking-tight text-brand-white">
                {feature.title}
              </h3>
              <p className="mt-3 max-w-md text-brand-silver">{feature.body}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {feature.tags?.map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-brand-silver/15 px-3 py-1 text-xs text-brand-silver"
                >
                  {t}
                </span>
              ))}
            </div>
          </SpotlightCard>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.06}>
          <SpotlightCard className="flex h-full flex-col p-7">
            <CapIcon name={secondary.icon} />
            <h3 className="mt-5 text-xl font-semibold tracking-tight text-brand-white">
              {secondary.title}
            </h3>
            <p className="mt-3 text-brand-silver">{secondary.body}</p>
          </SpotlightCard>
        </Reveal>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {rest.map((cap, i) => (
          <Reveal key={cap.title} delay={i * 0.06}>
            <SpotlightCard className="h-full p-6">
              <CapIcon name={cap.icon} />
              <h3 className="mt-4 text-base font-semibold text-brand-white">{cap.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-silver">{cap.body}</p>
            </SpotlightCard>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ------------------------------ process ---------------------------------- */

function ProcessSection() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 75%', 'end 65%'] });
  const lineScale = useSpring(scrollYProgress, { stiffness: 80, damping: 24, mass: 0.4 });

  return (
    <Section>
      <Reveal>
        <SectionHeading
          title="Cómo trabajamos."
          lead="De la solicitud a la entrega, un proceso claro y sin fricción."
        />
      </Reveal>
      <div ref={ref} className="relative mt-14">
        {/* The connecting rail draws left-to-right as the section scrolls into view. */}
        <div className="absolute inset-x-0 top-6 hidden h-px bg-brand-silver/15 lg:block" aria-hidden="true" />
        {reduced ? (
          <div className="absolute inset-x-0 top-6 hidden h-px bg-brand-accent lg:block" aria-hidden="true" />
        ) : (
          <motion.div
            style={{ scaleX: lineScale }}
            className="absolute inset-x-0 top-6 hidden h-px origin-left bg-brand-accent lg:block"
            aria-hidden="true"
          />
        )}
        <ol className="grid gap-10 lg:grid-cols-3">
          {STEPS.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.1}>
              <li className="relative">
                <span className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-brand-accent/40 bg-brand-bg text-lg font-semibold text-brand-accent">
                  {i + 1}
                </span>
                <h3 className="mt-5 text-lg font-semibold tracking-tight text-brand-white">
                  {step.title}
                </h3>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-brand-silver">{step.body}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </Section>
  );
}

const STEPS = [
  {
    title: 'Solicita tu cuenta',
    body: 'Verificamos tu empresa y definimos términos de crédito a tu medida.',
  },
  {
    title: 'Compra con confianza',
    body: 'Disponibilidad en tiempo real, precio en USD y VES, y tu crédito a la vista.',
  },
  {
    title: 'Recibe y paga',
    body: 'Despacho con seguimiento; sube tu comprobante de pago y listo, sin llamadas.',
  },
];

/* ------------------------------ platform --------------------------------- */

function PlatformSection() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const previewY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <Section muted id="plataforma">
      <div ref={ref} className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <SectionHeading
            align="left"
            title="Pedidos, crédito e inventario en un solo lugar."
            lead="Clientes, vendedores y distribuidores operan sobre la misma plataforma: disponibilidad en tiempo real, estado de crédito y comprobantes de pago, sin fricción."
          />
          <ul className="mt-6 space-y-3">
            {PLATFORM_POINTS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-brand-silver">
                <CheckMark />
                {p}
              </li>
            ))}
          </ul>
          <Link
            to="/login"
            className="press mt-8 inline-flex h-11 items-center justify-center rounded-md bg-brand-accent px-5 text-sm font-semibold text-on-accent transition-colors hover:bg-accent-hover"
          >
            Iniciar sesión
          </Link>
        </Reveal>

        <motion.div style={reduced ? undefined : { y: previewY }} className="[perspective:1800px]">
          <div className="tilt-3d">
            <PlatformPreview />
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

/* ------------------------------ industries ------------------------------- */

function IndustriesSection() {
  return (
    <Section id="industrias">
      <Reveal>
        <SectionHeading
          title="Del taller a la planta, a la flota."
          lead="Cobertura para las operaciones que mantienen al país en movimiento."
        />
      </Reveal>
      <div className="mt-10 flex flex-wrap gap-3">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind} delay={i * 0.03}>
            <span className="press inline-flex cursor-default items-center gap-2 rounded-full border border-brand-silver/20 bg-brand-white/5 px-4 py-2 text-sm text-brand-silver transition-colors hover:border-brand-accent/50 hover:text-brand-white">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-accent/70" />
              {ind}
            </span>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* --------------------------------- CTA ----------------------------------- */

function FinalCta() {
  return (
    <Section>
      <Reveal>
        <div className="hero-aurora relative overflow-hidden rounded-xl border border-brand-accent/25 bg-brand-primary px-6 py-16 text-center sm:px-12 sm:py-20">
          <div className="relative">
            <h2 className="mx-auto max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl" style={{ textWrap: 'balance' }}>
              ¿Listo para trabajar con Lombok?
            </h2>
            <p className="mx-auto mt-4 max-w-md text-brand-silver">
              Solicita una cuenta o conviértete en distribuidor. Te contactamos para completar la
              verificación.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <MagneticButton
                to="/request-account"
                className="press inline-flex h-12 items-center justify-center rounded-md bg-brand-accent px-6 text-sm font-semibold text-on-accent shadow-[0_10px_40px_-12px_rgba(216,164,49,0.7)] transition-colors hover:bg-accent-hover"
              >
                Solicitar cuenta
              </MagneticButton>
              <Link
                to="/login"
                className="press inline-flex h-12 items-center justify-center rounded-md border border-brand-silver/30 px-6 text-sm font-semibold text-brand-white transition-colors hover:border-brand-silver/60 hover:bg-brand-white/5"
              >
                Contactar ventas
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

/* ----------------------------- layout helpers ---------------------------- */

function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-brand-silver/10 bg-brand-bg/70 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Logo variant="light" className="h-8" />
        <nav className="hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-brand-silver transition-colors hover:text-brand-white"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="press hidden h-10 items-center rounded-md px-4 text-sm font-medium text-brand-silver transition-colors hover:text-brand-white sm:inline-flex"
          >
            Iniciar sesión
          </Link>
          <Link
            to="/request-account"
            className="press inline-flex h-10 items-center rounded-md bg-brand-accent px-4 text-sm font-semibold text-on-accent transition-colors hover:bg-accent-hover"
          >
            Solicitar cuenta
          </Link>
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-brand-silver/10 bg-brand-bg">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-14 sm:grid-cols-2 lg:grid-cols-4">
        <div className="lg:col-span-2">
          <Logo variant="light" className="h-8" />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-brand-silver">
            Distribución premium de lubricantes y productos industriales para las Américas.
            Construido para escalar, operado con confianza.
          </p>
        </div>
        {FOOTER_COLUMNS.map((col) => (
          <div key={col.title}>
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-silver/60">
              {col.title}
            </h3>
            <ul className="mt-4 space-y-3">
              {col.links.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-sm text-brand-silver transition-colors hover:text-brand-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-brand-silver/10">
        <div className="mx-auto max-w-6xl px-6 py-6">
          <p className="text-xs text-brand-silver/70">
            © {new Date().getFullYear()} Lombok Global Lubricants. Distribución premium.
          </p>
        </div>
      </div>
    </footer>
  );
}

function Section({
  children,
  muted = false,
  id,
}: {
  children: ReactNode;
  muted?: boolean;
  id?: string;
}) {
  return (
    <section id={id} className={muted ? 'scroll-mt-20 bg-brand-primary/25' : 'scroll-mt-20'}>
      <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28">{children}</div>
    </section>
  );
}

/** Section heading with a short gold rule instead of the AI uppercase eyebrow. */
function SectionHeading({
  title,
  lead,
  align = 'left',
}: {
  title: string;
  lead?: string;
  align?: 'left' | 'center';
}) {
  return (
    <div className={align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
      <span
        className={`block h-px w-10 bg-brand-accent ${align === 'center' ? 'mx-auto' : ''}`}
        aria-hidden="true"
      />
      <h2
        className="mt-5 text-3xl font-semibold tracking-tight text-brand-white sm:text-4xl"
        style={{ textWrap: 'balance', letterSpacing: '-0.02em' }}
      >
        {title}
      </h2>
      {lead ? <p className="mt-4 leading-relaxed text-brand-silver">{lead}</p> : null}
    </div>
  );
}

/** Abstract, on-brand mock of the platform UI (no real data). */
function PlatformPreview() {
  return (
    <div className="surface-metallic rounded-xl p-3 shadow-lg">
      <div className="rounded-lg bg-brand-bg/70 p-4">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-danger" />
          <span className="h-2.5 w-2.5 rounded-full bg-warning" />
          <span className="h-2.5 w-2.5 rounded-full bg-success" />
          <span className="ml-3 text-xs text-brand-silver">Lombok Commerce OS</span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-brand-silver">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
            </span>
            En vivo
          </span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[
            { k: 'Pedidos hoy', v: '128' },
            { k: 'Saldo pendiente', v: '$42,5k' },
            { k: 'Crédito disponible', v: '$18,0k' },
          ].map((stat) => (
            <div key={stat.k} className="rounded-md bg-brand-white/5 p-3">
              <p className="text-[11px] text-brand-silver">{stat.k}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-brand-white">{stat.v}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-2">
          {[
            { name: 'Valvoline Premium Blue 15W-40', tag: 'Disponible hoy', tone: 'bg-success' },
            { name: 'ATF MaxLife', tag: '24 a 72 horas', tone: 'bg-info' },
            { name: 'Grasa industrial EP-2', tag: 'Bajo pedido', tone: 'bg-warning' },
          ].map((row) => (
            <div
              key={row.name}
              className="flex items-center justify-between rounded-md bg-brand-white/5 px-3 py-2"
            >
              <span className="truncate text-xs text-brand-white">{row.name}</span>
              <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] text-brand-silver">
                <span className={`h-2 w-2 rounded-full ${row.tone}`} />
                {row.tag}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- iconography ----------------------------- */

/** Minimal 1.6px line icons (abstract, not cliche). Inherit currentColor. */
function CapIcon({ name }: { name: IconName }) {
  return (
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-brand-accent/25 bg-brand-accent/10 text-brand-accent">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {ICON_PATHS[name]}
      </svg>
    </span>
  );
}

function CheckMark() {
  return (
    <svg className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

type IconName = 'sourcing' | 'credit' | 'logistics' | 'portals' | 'payments' | 'scale';

const ICON_PATHS: Record<IconName, ReactNode> = {
  sourcing: (
    <>
      <path d="M3 7l9-4 9 4-9 4-9-4Z" />
      <path d="M3 7v10l9 4 9-4V7" />
      <path d="M12 11v10" />
    </>
  ),
  credit: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 15h4" />
    </>
  ),
  logistics: (
    <>
      <path d="M3 16V7h11v9" />
      <path d="M14 10h4l3 3v3h-7" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
    </>
  ),
  portals: (
    <>
      <rect x="3" y="4" width="7" height="7" rx="1.5" />
      <rect x="14" y="4" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="6" rx="1.5" />
      <rect x="14" y="14" width="7" height="6" rx="1.5" />
    </>
  ),
  payments: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M9.5 9.5a2.5 2 0 0 1 5 0c0 2.5-5 1.5-5 4a2.5 2 0 0 0 5 0" />
    </>
  ),
  scale: (
    <>
      <path d="M12 3v18" />
      <path d="M5 8l-2 5h4l-2-5Z" />
      <path d="M19 8l-2 5h4l-2-5Z" />
      <path d="M5 8h14" />
    </>
  ),
};

/* --------------------------------- data ---------------------------------- */

const HEADLINE: { t: string; gold?: boolean }[] = [
  { t: 'Distribución' },
  { t: 'premium', gold: true },
  { t: 'para' },
  { t: 'las' },
  { t: 'Américas' },
];

const NAV_LINKS = [
  { label: 'Qué hacemos', href: '#capacidades' },
  { label: 'Plataforma', href: '#plataforma' },
  { label: 'Industrias', href: '#industrias' },
];

const LIVE_METRICS = [
  { label: 'Hubs de distribución', to: 6, prefix: '', suffix: '', decimals: 0 },
  { label: 'SKUs en catálogo', to: 1240, prefix: '', suffix: '+', decimals: 0 },
  { label: 'Cumplimiento de entrega', to: 98.4, prefix: '', suffix: '%', decimals: 1 },
];

interface Capability {
  title: string;
  body: string;
  icon: IconName;
  tags?: string[];
}

const CAPABILITIES: Capability[] = [
  {
    title: 'Abastecimiento y distribución',
    body: 'Importación y distribución de marcas líderes con disponibilidad confiable y cobertura regional, desde el contenedor hasta el anaquel del cliente.',
    icon: 'sourcing',
    tags: ['Valvoline', 'Multimarca', 'Marca propia'],
  },
  {
    title: 'Crédito y términos comerciales',
    body: 'Términos flexibles (contado, Net 7, Net 15, Net 30) con control de crédito y estados de cuenta claros.',
    icon: 'credit',
  },
  {
    title: 'Logística y entrega',
    body: 'Reserva de stock, despacho y seguimiento, diseñados para operaciones B2B exigentes.',
    icon: 'logistics',
  },
  {
    title: 'Portales por rol',
    body: 'Clientes, vendedores y distribuidores con flujos a su medida, móvil primero.',
    icon: 'portals',
  },
  {
    title: 'Pagos locales y formales',
    body: 'Pago móvil, transferencia, Zelle y efectivo, con carga de comprobantes y revisión.',
    icon: 'payments',
  },
  {
    title: 'Escala multimarca',
    body: 'Arquitectura lista para múltiples marcas, marca propia y nuevas categorías industriales.',
    icon: 'scale',
  },
];

const PLATFORM_POINTS = [
  'Disponibilidad en tiempo real: hoy, en 24 a 72 horas, o bajo pedido.',
  'Crédito disponible y saldo pendiente visibles en cada pedido.',
  'Comprobantes de pago cargados y revisados sin llamadas ni correos.',
];

const CATEGORIES = [
  'Lubricantes para motor',
  'Fluidos de transmisión',
  'Grasas industriales',
  'Refrigerantes',
  'Hidráulicos',
  'Filtros',
  'Aditivos',
  'Marca propia',
];

const INDUSTRIES = [
  'Transporte y flotas',
  'Agroindustria',
  'Minería',
  'Construcción',
  'Manufactura',
  'Marino',
  'Talleres y retail',
  'Energía',
];

const FOOTER_COLUMNS = [
  {
    title: 'Plataforma',
    links: [
      { label: 'Iniciar sesión', to: '/login' },
      { label: 'Solicitar cuenta', to: '/request-account' },
      { label: 'Convertirse en distribuidor', to: '/request-account' },
    ],
  },
  {
    title: 'Empresa',
    links: [
      { label: 'Qué hacemos', to: '/' },
      { label: 'Industrias', to: '/' },
      { label: 'Contactar ventas', to: '/login' },
    ],
  },
];
